import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ShieldCheck, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { USER_ROLE } from '@/utils/constants';

const AdminPortalPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);

  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(loginData.email, loginData.password);
      if (result.user.role !== USER_ROLE.ADMIN) {
        toast.error('Access denied. Admin access only.');
        return;
      }
      toast.success('Admin login successful!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-8">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_broker-claims-1/artifacts/duvrk5eb_kind-insurance-logo.png" 
              alt="Kind Insurance Brokers" 
              className="h-20 w-auto"
            />
          </div>
          <div className="flex items-center justify-center gap-2 mb-3">
            <ShieldCheck className="h-6 w-6 text-red-500" />
            <span className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Admin Portal
            </span>
          </div>
          <div className="flex items-center justify-center gap-2 text-slate-400 mb-2">
            <Lock className="h-4 w-4" />
            <span className="text-sm font-semibold" style={{ fontFamily: 'Public Sans, sans-serif' }}>
              SYSTEM ADMINISTRATOR ACCESS
            </span>
          </div>
          <p className="text-xs text-slate-500" style={{ fontFamily: 'Public Sans, sans-serif' }}>
            Kind Insurance Brokers Limited
          </p>
        </div>

        <Card className="border-red-900/50 bg-slate-800/80 backdrop-blur-sm shadow-2xl">
          <CardHeader className="border-b border-slate-700">
            <CardTitle className="text-white text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Administrator Login
            </CardTitle>
            <CardDescription className="text-slate-400 text-center" style={{ fontFamily: 'Public Sans, sans-serif' }}>
              Enter admin credentials to access system management
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="admin-email" className="text-slate-300" style={{ fontFamily: 'Public Sans, sans-serif' }}>
                  Admin Email
                </Label>
                <Input
                  id="admin-email"
                  data-testid="admin-portal-email-input"
                  type="email"
                  placeholder="admin@portal.com"
                  className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  required
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-password" className="text-slate-300" style={{ fontFamily: 'Public Sans, sans-serif' }}>
                  Admin Password
                </Label>
                <Input
                  id="admin-password"
                  data-testid="admin-portal-password-input"
                  type="password"
                  placeholder="••••••••"
                  className="bg-slate-700/50 border-slate-600 text-white"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  required
                />
              </div>
              <Button 
                type="submit" 
                data-testid="admin-portal-login-button"
                className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold" 
                disabled={loading}
                style={{ fontFamily: 'Public Sans, sans-serif' }}
              >
                {loading ? 'Authenticating...' : 'Access Admin Portal'}
              </Button>
            </form>

            <div className="mt-6 p-4 bg-slate-900/50 rounded-lg border border-slate-700">
              <p className="text-xs text-slate-400 mb-2 font-semibold" style={{ fontFamily: 'Public Sans, sans-serif' }}>
                DEFAULT ADMIN CREDENTIALS:
              </p>
              <div className="space-y-1 text-xs font-mono text-slate-300">
                <div>Email: <span className="text-red-400">admin@portal.com</span></div>
                <div>Password: <span className="text-red-400">Admin@2026</span></div>
              </div>
              <p className="text-xs text-amber-400 mt-2" style={{ fontFamily: 'Public Sans, sans-serif' }}>
                ⚠️ Change default password after first login
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-4 justify-center mt-6">
          <Button
            variant="link"
            className="text-slate-400 hover:text-white text-sm"
            onClick={() => navigate('/auth')}
          >
            Client Portal →
          </Button>
          <Button
            variant="link"
            className="text-slate-400 hover:text-white text-sm"
            onClick={() => navigate('/admin/auth')}
          >
            Broker Portal →
          </Button>
        </div>

        <p className="text-center text-xs text-slate-600 mt-6" style={{ fontFamily: 'Public Sans, sans-serif' }}>
          © 2026 Kind Insurance Brokers Limited. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default AdminPortalPage;
